import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { APP_VERSION, BUILD_DATE } from '../../version';
import {
  LayoutDashboard,
  Layers,
  Image,
  Users,
  Briefcase,
  Star,
  MessageSquare,
  Link2,
  Settings,
  LogOut,
  Eye,
  ChevronLeft,
  Menu,
  X,
  CreditCard,
  Inbox,
  ImageIcon,
  Clock,
  FolderOpen,
  FileText,
  Scale,
  Bell,
  ListOrdered,
  Play,
  FileDown,
  Globe,
  TrendingUp,
  Type,
  Mail,
  BookOpen,
  Trophy,
  ChevronDown,
  ChevronRight,
  Search
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import api from '../../lib/axios';

const CMSLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteLogos, setSiteLogos] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    general: true,
    content: true,
    marketing: false,
    config: false
  });

  // Load site logos
  useEffect(() => {
    const loadLogos = async () => {
      try {
        const res = await api.get('/cms/logos');
        console.log('CMSLayout logos loaded:', res.data);
        if (res.data) setSiteLogos(res.data);
      } catch (error) {
        console.error('Error loading logos:', error);
      }
    };
    loadLogos();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/site');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Navigation groupée par objectifs avec couleurs
  const cmsNavigationGroups = [
    {
      id: 'general',
      label: '📊 Général',
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      items: [
        { to: '/cms', icon: LayoutDashboard, label: 'Tableau de bord', end: true },
        { to: '/cms/requests', icon: Inbox, label: 'Demandes' },
        { to: '/cms/media', icon: FolderOpen, label: 'Médiathèque' },
      ]
    },
    {
      id: 'content',
      label: '✏️ Contenu du site',
      color: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/30',
      items: [
        { to: '/cms/hero', icon: Layers, label: 'Hero & Navigation' },
        { to: '/cms/services', icon: Briefcase, label: 'Services' },
        { to: '/cms/timeline', icon: ListOrdered, label: 'Timeline Com\'' },
        { to: '/cms/subscriptions', icon: CreditCard, label: 'Forfaits hébergement' },
        { to: '/cms/team', icon: Users, label: 'Équipe' },
        { to: '/cms/team-page-config', icon: Play, label: 'Vidéo équipe' },
        { to: '/cms/portfolio', icon: Image, label: 'Réalisations' },
        { to: '/cms/partners', icon: Star, label: 'Partenaires' },
        { to: '/cms/testimonials', icon: MessageSquare, label: 'Témoignages' },
        { to: '/cms/google-reviews', icon: Star, label: 'Avis Google' },
        { to: '/cms/blog', icon: FileText, label: 'Blog' },
        { to: '/cms/knowledge', icon: BookOpen, label: 'Base de connaissances' },
        { to: '/cms/pages', icon: Scale, label: 'Pages légales' },
      ]
    },
    {
      id: 'marketing',
      label: '🚀 Marketing & Engagement',
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/30',
      items: [
        { to: '/cms/game-contests', icon: Trophy, label: 'Jeux Concours', badge: 'NEW' },
        { to: '/cms/popup', icon: Bell, label: 'Popup accueil' },
        { to: '/cms/seo-generator', icon: Globe, label: 'Générateur SEO' },
        { to: '/cms/brochure', icon: FileDown, label: 'Brochure PDF' },
        { to: '/cms/brochure-config', icon: Settings, label: 'Config Brochure' },
        { to: '/cms/social', icon: Link2, label: 'Réseaux sociaux' },
      ]
    },
    {
      id: 'config',
      label: '⚙️ Configuration',
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      items: [
        { to: '/cms/seo', icon: Search, label: 'SEO & Référencement', badge: 'NEW' },
        { to: '/cms/logos', icon: ImageIcon, label: 'Logos' },
        { to: '/cms/schedule', icon: Clock, label: 'Horaires' },
        { to: '/cms/key-stats', icon: TrendingUp, label: 'Chiffres clés' },
        { to: '/cms/site-content', icon: Type, label: 'Textes du site' },
        { to: '/cms/settings', icon: Settings, label: 'Header & Footer' },
        { to: '/cms/smtp', icon: Mail, label: 'Configuration Email' },
      ]
    }
  ];

  // Flat navigation for mobile
  const cmsNavigation = cmsNavigationGroups.flatMap(g => g.items);

  // Logo component
  const SiteLogo = ({ size = 'md', darkBg = true }) => {
    const logoPath = darkBg ? siteLogos?.logo_dark : siteLogos?.logo_light;
    const sizeClasses = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
    
    // Build full URL with backend prefix
    const logoUrl = logoPath 
      ? (logoPath.startsWith('http') ? logoPath : `${process.env.REACT_APP_BACKEND_URL}${logoPath}`)
      : null;
    
    console.log('SiteLogo:', { darkBg, logoPath, logoUrl, siteLogos });
    
    if (logoUrl) {
      return (
        <div className={cn(sizeClasses, "rounded-full overflow-hidden flex items-center justify-center")}>
          <img 
            src={logoUrl} 
            alt="Logo" 
            className="w-full h-full object-contain" 
            onError={(e) => console.error('Logo failed to load:', logoUrl)}
          />
        </div>
      );
    }
    
    return (
      <div className={cn(sizeClasses, "rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center")}>
        <Layers className={size === 'sm' ? "w-4 h-4 text-white" : "w-5 h-5 text-white"} />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Header - Always visible on mobile */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-[100] bg-slate-900 border-b border-slate-800 shadow-lg">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <SiteLogo size="sm" darkBg={true} />
            <span className="font-bold text-white">BackOffice</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleLogout}
              className="p-2 text-red-400 hover:text-red-300 hover:bg-slate-800 rounded-lg"
              aria-label="Déconnexion"
              title="Déconnexion"
            >
              <LogOut className="w-5 h-5" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-white hover:bg-slate-800 rounded-lg"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[90] bg-slate-900/95 backdrop-blur-sm pt-16 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {cmsNavigation.map(item => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-4 py-3 rounded-lg transition-all",
                      isActive
                        ? "bg-gradient-to-r from-cyan-500/20 to-blue-600/20 text-cyan-400 border-l-4 border-cyan-400"
                        : "text-slate-400 hover:text-white hover:bg-slate-800"
                    )
                  }
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
            <div className="pt-4 border-t border-slate-800 mt-4">
              <button
                onClick={() => { navigate('/site'); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 w-full"
              >
                <Eye className="w-5 h-5" />
                <span>Voir le site</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:text-red-300 hover:bg-slate-800 w-full"
              >
                <LogOut className="w-5 h-5" />
                <span>Déconnexion</span>
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed top-0 left-0 h-full w-64 flex-col bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 border-r border-slate-800">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <SiteLogo size="md" darkBg={true} />
            <div>
              <h1 className="font-bold text-white text-lg">BackOffice</h1>
              <p className="text-xs text-slate-500">Gestion du site</p>
            </div>
          </div>
        </div>

        {/* Navigation groupée */}
        <nav className="flex-1 p-3 space-y-2 overflow-y-auto">
          {cmsNavigationGroups.map(group => (
            <div key={group.id} className={cn("rounded-lg border", group.borderColor, group.bgColor)}>
              <button
                onClick={() => toggleSection(group.id)}
                className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-white hover:bg-white/5 rounded-t-lg"
              >
                <span>{group.label}</span>
                {expandedSections[group.id] ? (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                )}
              </button>
              {expandedSections[group.id] && (
                <div className="px-1 pb-2 space-y-0.5">
                  {group.items.map(item => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2 px-3 py-1.5 rounded-md transition-all text-xs font-medium",
                            isActive
                              ? `bg-gradient-to-r ${group.color} text-white shadow-sm`
                              : "text-slate-300 hover:text-white hover:bg-white/10"
                          )
                        }
                      >
                        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="flex-1">{item.label}</span>
                        {item.badge && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded">
                            {item.badge}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-slate-800 space-y-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/site')}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <Eye className="w-4 h-4 mr-3" />
            Voir le site
          </Button>
          
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/50">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Déconnexion
          </Button>
          
          {/* Version info */}
          <div className="mt-4 pt-3 border-t border-slate-800 text-center">
            <p className="text-[10px] text-slate-600">
              Version {APP_VERSION} • {BUILD_DATE}
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64 min-h-screen">
        <main className="p-4 lg:p-8 pt-20 lg:pt-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default CMSLayout;
